<template>
  <div class="d-flex align-items-center">
    <!-- Quick Add Trigger Button -->
    <li class="nav-item nav-icon-item">
      <a class="nav-link nav-icon-link quick-add-btn" @click.prevent="showQuickAdd = true" href="javascript:void(0)" title="Quick Add">
        <i class="fas fa-plus"></i>
      </a>
    </li>

    <!-- Quick Add Full Page Popup -->
    <Teleport to="body">
      <transition name="qa-fade">
        <div v-if="showQuickAdd" class="quick-add-overlay" @click="showQuickAdd = false">
          <div class="quick-add-panel" @click.stop>

            <!-- Header: Search & Close -->
            <div class="qa-header">
              <div class="qa-search-wrap">
                <i class="fas fa-search qa-search-icon"></i>
                <input
                  v-model="qaSearchQuery"
                  type="text"
                  class="qa-search-input"
                  placeholder="Search for activities..."
                  @keydown.esc="showQuickAdd = false"
                  :disabled="qaLoading"
                />
              </div>
              <button class="qa-close-btn" @click="showQuickAdd = false" :disabled="qaLoading">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Tabs -->
            <div class="qa-tabs">
              <button
                  v-for="tab in qaTabs"
                  :key="tab.id"
                  :class="['qa-tab', { active: qaActiveTab.id === tab.id }]"
                  @click="onTabClick(tab)"
                  :disabled="qaLoading"
              >
                {{ tab.title }}
              </button>
            </div>

            <!-- Body -->
            <div class="qa-body">

              <Loader v-if="qaLoading" />

              <template v-else>
                <div class="qa-grid">
                  <div
                    v-for="item in filteredQaItems"
                    :key="item.label"
                    class="qa-grid-item"
                    @click="showQuickAdd = false"
                  >
                    <component
                        :is="currentComponent"
                        :page="item"
                    />
                  </div>
                </div>

                <div v-if="filteredQaItems.length === 0" class="qa-empty">
                  No results found for "{{ qaSearchQuery }}"
                </div>
              </template>
            </div>

          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import { ref, computed, watch, onUnmounted } from 'vue';
import handleCompany from '@/services/modules/company';
import { getQuickAddCache, setQuickAddCache } from '@/services/utils/helpers/cacheHelper';
import { useRoute } from 'vue-router';
import PageCard from '@/components/molecule/company/PageCard';
import ProcurePageCard from '@/components/molecule/company/ProcurePageCard';
import Loader from "@/components/atom/LoaderComponent";
import {useStore} from "vuex";

export default {
  name: 'QuickAdd',
  props: {
    modules: {
      type: Array,
      default: () => []
    }
  },
  components: {
    PageCard,
    ProcurePageCard,
    Loader,
  },

  computed: {
    currentComponent() {
      return this.$route.params.moduleId == 1 ? 'PageCard' : 'ProcurePageCard';
    }
  },

  setup(props) {
    const route         = useRoute();
    const store         = useStore()
    const qaLoading     = ref(false);
    const qaItemsCache  = ref({});
    const showQuickAdd  = ref(false);
    const qaSearchQuery = ref('');
    const user = computed(() => store.state.user.user);

    const { fetchSidebar } = handleCompany();

    const qaTabs = computed(() =>
        props.modules.map(m => ({ id: m.id, title: m.name }))
    );

    const qaActiveTab = ref(null);

    const qaActiveItems = computed(() =>
        qaActiveTab.value ? (qaItemsCache.value[qaActiveTab.value.id] ?? []) : []
    );

    const filteredQaItems = computed(() => {
      const query = qaSearchQuery.value.toLowerCase().trim();
      if (!query) return qaActiveItems.value;
      return qaActiveItems.value.filter(item =>
          item.name.toLowerCase().includes(query)
      );
    });

    async function fetchTabItems(tab) {
      let userId = user.value ? user.value.id : null;
      let companyId = route.params.companyId;
      const CACHE_KEY = `qa_cache_company_${companyId}_user_${userId}_module_${tab.id}`;
      const CACHE_TTL_MS = 6 * 60 * 60 * 1000;

      const cachedItems = getQuickAddCache(store, 'dashboard', CACHE_KEY, CACHE_TTL_MS);

      if (cachedItems) {
        qaItemsCache.value = { ...qaItemsCache.value, [tab.id]: cachedItems };
        return;
      }
      if (! cachedItems){
        delete qaItemsCache.value[tab.id];
      }

      qaLoading.value = true;
      try {
        const res = await fetchSidebar(
            `?module_id=${tab.id}&company_id=${route.params.companyId}`
        );
        if (!res.status){
          qaItemsCache.value = { ...qaItemsCache.value, [tab.id]: [] };
          return
        }

        const items = (res.data)
            .filter(page => page.status === 'active' && page.is_dashboard !== 1)
            .flatMap(menu =>
            (menu.submenus ?? []).flatMap(submenu =>
                (submenu.pages ?? [])
                    .filter(page => page.status === 'active' && page.is_card !== 1)
                    .map(page => ({
                      id: page.id,
                      name: page.name,
                      icon:  page.icon,
                      route: page.route,
                      status: page.status,
                    }))
            )
        );

        qaItemsCache.value = { ...qaItemsCache.value, [tab.id]: items };
        setQuickAddCache(store, 'dashboard', CACHE_KEY, items);
      } catch (err) {
        qaItemsCache.value = { ...qaItemsCache.value, [tab.id]: [] };
      } finally {
        qaLoading.value = false;
      }
    }

    function onTabClick(tab) {
      qaActiveTab.value   = tab;
      qaSearchQuery.value = '';
      fetchTabItems(tab);
    }

    watch(
        qaTabs,
        (tabs) => {
          if (tabs.length && !qaActiveTab.value) {
            qaActiveTab.value = tabs[0];
          }
        },
        { immediate: true }
    );

    watch(showQuickAdd, (isOpen) => {
      document.body.classList.toggle('overflow-hidden', isOpen);
        if (isOpen && !qaActiveTab.value && qaTabs.value.length) {
          qaActiveTab.value = qaTabs.value[0];
        }
        if (isOpen && qaActiveTab.value) {
          fetchTabItems(qaActiveTab.value);
        }
    });

    onUnmounted(() => {
      document.body.classList.remove('overflow-hidden');
    });

    return {
      showQuickAdd,
      qaSearchQuery,
      qaLoading,
      qaActiveTab,
      qaTabs,
      filteredQaItems,
      onTabClick,
    };
  },
};
</script>

<style scoped>
.nav-icon-item { display: flex; align-items: center; }
.nav-icon-link {
  display: flex; align-items: center; justify-content: center;
  width: 38px; height: 38px;
  padding: 0 !important;
  border-radius: 10px;
  color: #6366f1;
  background-color: transparent;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.quick-add-btn {
  background-color: #3b7ddd !important;
  color: #ffffff !important;
  width: 28px !important;
  height: 28px !important;
  border-radius: 8px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  transition: all 0.2s ease;
  padding: 0 !important;
  margin: 0 4px;
}

.quick-add-btn:hover {
  background-color: #a1a4a8 !important;
  transform: scale(1.05);
}

.quick-add-btn i {
  font-size: 1.1rem;
}

.quick-add-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: #ffffff;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.quick-add-panel {
  width: 100vw;
  height: 100vh;
  background: #ffffff;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.qa-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.qa-search-wrap {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  max-width: 800px;
  margin: 0 auto;
}

.qa-search-icon {
  position: absolute;
  left: 16px;
  color: #94a3b8;
  font-size: 1.1rem;
}

.qa-search-input {
  width: 100%;
  height: 44px;
  padding: 0 16px 0 48px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  color: #1e293b;
  outline: none;
  transition: all 0.2s;
}

.qa-search-input:focus {
  background: #ffffff;
  border-color: #4e92ff;
  box-shadow: 0 0 0 4px rgba(78, 146, 255, 0.1);
}

.qa-close-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  border: none;
  border-radius: 50%;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.qa-close-btn:hover {
  background: #fee2e2;
  color: #ef4444;
  transform: rotate(90deg);
}

.qa-tabs {
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  padding: 10px 24px;
  background: #fcfcfd;
  border-bottom: 1px solid #f1f5f9;
  overflow-x: auto;
  scrollbar-width: none;
  flex-shrink: 0;
  -webkit-overflow-scrolling: touch;
}

.qa-icon-img {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.qa-tabs::-webkit-scrollbar { display: none; }

@media (min-width: 641px) {
  .qa-tabs {
    justify-content: center;
  }
}

.qa-tab {
  padding: 6px 18px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}

.qa-tab:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.qa-tab.active {
  background: #4e92ff;
  color: #ffffff;
}

.qa-body {
  flex: 1;
  padding: 32px 24px;
  overflow-y: auto;
  background: #ffffff;
}

.qa-section-label {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
  margin-bottom: 24px;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
}

.qa-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 120px));
  gap: 24px 16px;
  max-width: 1172px;
  margin: 0 auto;
  justify-content: center;
}

.qa-grid-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  cursor: pointer;
  transition: transform 0.2s;
  width: 120px;
  text-align: center;
}

.qa-grid-item:hover {
  transform: translateY(-6px);
}

.qa-icon-box {
  width: 90px;
  height: 90px;
  background: #fff9e6;
  border: 1px solid #fef3c7;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FFFFFF;
  font-size: 2rem;
  transition: all 0.2s;
}

.qa-grid-item:hover .qa-icon-box {
  background: #fef3c7;
  box-shadow: 0 12px 20px -5px rgba(217, 119, 6, 0.15);
}

.qa-item-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
  text-align: center;
  line-height: 1.4;
}

.qa-empty {
  text-align: center;
  padding: 60px;
  color: #94a3b8;
  font-size: 1.1rem;
}

.qa-footer {
  padding: 20px 24px;
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
  text-align: center;
  font-size: 0.9rem;
  color: #64748b;
  flex-shrink: 0;
}

.qa-settings-link {
  color: #4e92ff;
  font-weight: 600;
  text-decoration: none;
}

.qa-settings-link:hover {
  text-decoration: underline;
}

.qa-fade-enter-active, .qa-fade-leave-active { transition: opacity 0.25s ease; }
.qa-fade-enter-from, .qa-fade-leave-to { opacity: 0; }

@media (max-width: 640px) {
  .qa-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 100px));
    gap: 16px 8px;
  }
  .qa-icon-box { width: 80px; height: 80px; font-size: 1.75rem; }
  .qa-header { padding: 12px 16px; }
  .qa-tabs { padding: 8px 16px; }
}
</style>
